/**
 * Authentication: token storage + global axios wiring.
 *
 * Every page in this app calls `axios` directly rather than a shared client,
 * so auth is installed as global axios interceptors from one place
 * (see index.js). No per-page changes are required.
 *
 * - Request:  attach `Authorization: Bearer <token>` to same-API requests.
 * - Response: on 401, clear the token and send the user to /login.
 */
import axios from 'axios';

const TOKEN_KEY = 'reipr_token';
const USER_KEY = 'reipr_user';

export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null; // private mode / storage disabled
  }
};

export const getUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const setSession = (token, user) => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    /* storage unavailable — session lasts for this page load only */
  }
};

export const clearSession = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
};

export const isAuthenticated = () => Boolean(getToken());

/** Install global axios interceptors. Call once at app startup. */
export const installAuthInterceptors = () => {
  axios.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        clearSession();
        const { pathname, search } = window.location;
        if (pathname !== '/login') {
          const next = encodeURIComponent(pathname + search);
          window.location.replace(`/login?next=${next}`);
        }
      }
      return Promise.reject(error);
    }
  );
};
